import Link from 'next/link';
import FormGroup from '@/components/forms/form-group';
import FormGroupPrefix from '@/components/forms/form-group-prefix';
import FormField from '@/components/forms/form-field';
import FormError from '@/components/forms/form-error';
import Button from '@/components/button';
import styles from './login-form.module.css';
import Validators from '@/utils/validators';
import * as yup from 'yup';
import type {NetworkError} from '@/utils/axios';
import axios, {parseAxiosErrorMessage} from '@/utils/axios';
import {FiLock, FiMail} from 'react-icons/fi';
import {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {ErrorMessage, Field, Form, Formik} from 'formik';
import type {WithAuthProps} from '@/contexts/auth-context';
import {withAuth} from '@/contexts/auth-context';
import {withRouter} from 'next/router';
import type {WithAlertsProps} from '@/contexts/alerts-context';
import {withAlerts} from '@/contexts/alerts-context';
import type {WrappedComponentProps as IntlProps} from 'react-intl/lib';
import type {WithRouterProps} from 'next/dist/client/with-router';
import {User} from '@soubul/ts-commons';

const validation: yup.ObjectSchema<{}> = yup.object().shape({
  email: Validators.requiredEmailIfNoPhoneNumber,
  password: Validators.password,
});

interface FormValues {
  readonly email: string;
  readonly password: string;
}

type Props = IntlProps & WithAuthProps & WithAlertsProps & WithRouterProps;

class EmailLoginForm extends Component<Props> {
  private readonly defaultFormValues: FormValues;

  constructor(props: Props) {
    super(props);
    this.defaultFormValues = {email: '', password: ''};
  }

  public render(): JSX.Element {
    const {formatMessage: t} = this.props.intl;

    return (
      <div className={styles.form_container}>
        <Formik initialValues={this.defaultFormValues} validationSchema={validation} onSubmit={this.handleSubmit}>
          {({isSubmitting, errors, touched}) => (
            <Form>
              <FormGroup invalid={!!errors.email && touched.email}>
                <FormGroupPrefix>
                  <FiMail />
                </FormGroupPrefix>
                <Field type="email" name="email" placeholder={t({id: 'words.email'})} component={FormField} />
              </FormGroup>
              <ErrorMessage name="email" component={FormError} />

              <FormGroup invalid={!!errors.password && touched.password}>
                <FormGroupPrefix>
                  <FiLock />
                </FormGroupPrefix>
                <Field type="password" name="password" placeholder={t({id: 'words.password'})} component={FormField} />
              </FormGroup>
              <ErrorMessage name="password" component={FormError} />

              <div className={styles.submit_button_container}>
                <Button fullWidth type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <FormattedMessage id="words.loading" /> : <FormattedMessage id="words.login" />}
                </Button>
              </div>

              <Link href="/reset-password" passHref>
                <a className={styles.password_reset_link}>
                  <FormattedMessage id="pages.login.forgotten_password" />
                </a>
              </Link>
            </Form>
          )}
        </Formik>
      </div>
    );
  }

  /** @internal */
  private handleSubmit = async (values: FormValues) => {
    try {
      const res = await axios.post('/auth/login', values);
      if (res.data) {
        const user = User.fromJSON(res.data);
        this.props.alerts.success('signed in successfully');
        await this.props.session.setUser(user);
      }
    } catch (e) {
      this.showErrorMessage(parseAxiosErrorMessage(e));
    }
  };

  /** @internal */
  private showErrorMessage = (err: NetworkError) => {
    const {formatMessage: t} = this.props.intl;
    switch (err.code) {
      case 'auth/user-not-found':
        return this.props.alerts.error(t({id: 'errors.user_not_found'}), 10000);
      default:
        return this.props.alerts.error(t({id: 'errors.unable_to_login'}));
    }
  };
}

export default withAlerts(withAuth(withRouter(injectIntl(EmailLoginForm))));
